import { useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PropertyAdminPanel from "../components/realEstate/propertyDetail/PropertyAdminPanel";
import PropertyAmenities from "../components/realEstate/propertyDetail/PropertyAmenities";
import PropertyDetailErrorState from "../components/realEstate/propertyDetail/PropertyDetailErrorState";
import PropertyDetailHero from "../components/realEstate/propertyDetail/PropertyDetailHero";
import PropertyDetailNotFound from "../components/realEstate/propertyDetail/PropertyDetailNotFound";
import PropertyDetailSkeleton from "../components/realEstate/propertyDetail/PropertyDetailSkeleton";
import PropertyHistoryTimeline from "../components/realEstate/propertyDetail/PropertyHistoryTimeline";
import PropertyEnvironmentalContextCard from "../components/realEstate/propertyDetail/PropertyEnvironmentalContextCard";
import PropertyWeatherRiskCard from "../components/realEstate/propertyDetail/PropertyWeatherRiskCard";
import PropertyWeatherWatchSection from "../components/realEstate/propertyDetail/PropertyWeatherWatchSection";
import PropertyEnvironmentalModelCard from "../components/realEstate/propertyDetail/PropertyEnvironmentalModelCard";
import PropertyInsights from "../components/realEstate/propertyDetail/PropertyInsights";
import PropertyLocationCard from "../components/realEstate/propertyDetail/PropertyLocationCard";
import PropertyMarketFitCard from "../components/realEstate/propertyDetail/PropertyMarketFitCard";
import PropertyOverview from "../components/realEstate/propertyDetail/PropertyOverview";
import PropertyPriceHistoryChart from "../components/realEstate/propertyDetail/PropertyPriceHistoryChart";
import PropertySourcesCard from "../components/realEstate/propertyDetail/PropertySourcesCard";
import { usePropertyDetail } from "../hooks/usePropertyDetail";
import {
  consumePropertyDetailReturnState,
  getPropertyDetailUrl,
} from "../utils/propertyDetailNavigation";

function buildBreadcrumb(property) {
  const parts = ["Real Estate"];

  if (property?.municipality || property?.city) {
    parts.push(property.municipality || property.city);
  }

  if (property?.neighborhood) {
    parts.push(property.neighborhood);
  }

  parts.push("Property detail");
  return parts;
}

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { property, loading, error, notFound, reload } = usePropertyDetail(propertyId);

  const handleBack = useCallback(() => {
    const returnState = consumePropertyDetailReturnState();

    if (returnState?.pathname) {
      const target = `${returnState.pathname}${returnState.search || ""}${returnState.hash || ""}`;
      navigate(target, {
        state: { propertyDetailRestore: returnState },
      });
      return;
    }

    navigate("/");
  }, [navigate]);

  const handleShare = useCallback(async () => {
    const url = getPropertyDetailUrl(propertyId);

    if (navigator.share) {
      try {
        await navigator.share({
          title: property?.title || "Property listing",
          url,
        });
        return;
      } catch {
        // Fall through to clipboard.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Ignore clipboard failures.
    }
  }, [property?.title, propertyId]);

  const breadcrumbs = useMemo(() => buildBreadcrumb(property), [property]);

  return (
    <div className="midpoint-app property-detail-app">
      <header className="app-header midpoint-header property-detail-app__header">
        <div className="midpoint-header-inner app-header-inner">
          <Link to="/" className="app-brand midpoint-brand">
            <span className="app-title midpoint-title">Midpoint Mapping</span>
          </Link>
        </div>
      </header>

      <main className="property-detail-page" id="main-content">
        <div className="property-detail-page__top">
          <nav className="property-detail-breadcrumb" aria-label="Breadcrumb">
            <ol>
              {breadcrumbs.map((part, index) => (
                <li key={`${part}-${index}`}>
                  {index === 0 ? (
                    <Link to="/">{part}</Link>
                  ) : index === breadcrumbs.length - 1 ? (
                    <span aria-current="page">{part}</span>
                  ) : (
                    <span>{part}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          <div className="property-detail-page__actions">
            <button
              type="button"
              className="midpoint-share-button"
              onClick={handleBack}
            >
              Back to results
            </button>
          </div>
        </div>

        {loading ? <PropertyDetailSkeleton /> : null}

        {!loading && notFound ? <PropertyDetailNotFound onBack={handleBack} /> : null}

        {!loading && !notFound && error ? (
          <PropertyDetailErrorState message={error} onRetry={reload} onBack={handleBack} />
        ) : null}

        {!loading && !notFound && !error && property ? (
          <>
            <PropertyDetailHero property={property} onShare={handleShare} />

            <div className="property-detail-page__grid">
              <div className="property-detail-page__main">
                <PropertyOverview property={property} />
                <PropertyInsights insights={property.insights} />
                <PropertyPriceHistoryChart priceHistory={property.priceHistory} />
                <PropertyHistoryTimeline
                  listingHistory={property.listingHistory}
                  currency={property.currencyCode}
                />
                <PropertyAmenities amenityGroups={property.amenityGroups} />
              </div>

              <aside className="property-detail-page__aside">
                <PropertyLocationCard property={property} />
                <PropertyWeatherRiskCard
                  propertyId={property.id}
                  coordinates={property.location}
                />
                <PropertyWeatherWatchSection property={property} />
                <PropertyEnvironmentalContextCard environmentalRisk={property.environmentalRisk} />
                <PropertyEnvironmentalModelCard environmentalModel={property.environmentalModel} />
                <PropertyMarketFitCard marketFit={property.marketFit} />
                <PropertySourcesCard property={property} />
                <PropertyAdminPanel admin={property.admin} />
              </aside>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
};

export default PropertyDetailPage;
